import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FaArrowLeftLong } from "react-icons/fa6";
import AsyncSelect from "react-select/async";

import FormInput from "../../../components/common/FormInput/FormInput";
import UploadImage from "../../../components/common/UploadImage/UplaodImage";
import Button from "../../../components/common/Button/Button";

import {
  Menulist,
  getCategoryById,
  updateCategory,
} from "../../../api/service/products.service";

import { showToast } from "../../../components/common/ToastNotification/ToastNotification";

const UpdateProductListing = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);

  const [selectedMenu, setSelectedMenu] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);

  const [existingImage, setExistingImage] = useState("");
  const [existingIcon, setExistingIcon] = useState("");

  const [formData, setFormData] = useState({
    category_id: "",
    menu_id: null,
    title: "",
    sub_title: "",
    text: "",
    slug: "",
  });


  useEffect(() => {
    if (!id) return;

    const fetchCategoryDetails = async () => {
      try {
        setFetching(true);

        const response = await getCategoryById(id);
        const category = response?.data;

        setFormData({
          category_id: category.id,
          menu_id: category.menu_id || null,
          title: category.title || "",
          sub_title: category.sub_title || "",
          text: category.text || "",
          slug: category.slug || "",
        });

        setExistingImage(category.photo?.url || "");
        setExistingIcon(category.icon?.url || "");

 
        if (category.menu_id) {
          const menuRes = await Menulist({ page: 1, limit: 100 });

          const matchedMenu = menuRes?.data?.find(
            (menu) => menu.id === category.menu_id
          );

          if (matchedMenu) {
            setSelectedMenu({
              value: matchedMenu.id,
              label: matchedMenu.name,
            });
          }
        }
      } catch (error) {
        console.error(error);
        showToast.error("Failed to load category details");
      } finally {
        setFetching(false);
      }
    };

    fetchCategoryDetails();
  }, [id]);


  const loadMenuOptions = async (inputValue) => {
    try {
      const res = await Menulist({
        page: 1,
        limit: 10,
        search: inputValue || "",
      });

      return (res?.data || []).map((menu) => ({
        value: menu.id,
        label: menu.name,
      }));
    } catch (error) {
      console.error("Menu load error:", error);
      return [];
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) return showToast.error("Title required");
    if (!formData.sub_title.trim()) return showToast.error("Subtitle required");
    if (!formData.text.trim()) return showToast.error("Description required");

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("category_id", formData.category_id);
      fd.append("title", formData.title);
      fd.append("sub_title", formData.sub_title);
      fd.append("text", formData.text);
      fd.append("slug", formData.slug);
      fd.append("menu_id", formData.menu_id || "");

      if (selectedImage) fd.append("image", selectedImage);
      if (selectedIcon) fd.append("icon", selectedIcon);

      const res = await updateCategory(fd);
      showToast.success(res?.data?.message || "Category updated");

      navigate("/category");
    } catch (error) {
      console.error(error);
      showToast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  const showIconField = !!existingIcon;

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <FaArrowLeftLong
          style={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        />
        <h3 className="mb-0 ml-2">Update Product Category</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} md={showIconField ? 6 : 12} className="mb-3">
            <label className="form-label">Category Image</label>
            <UploadImage
              onFileSelect={setSelectedImage}
              existingImages={existingImage}
              multiple={false}
            />
          </Col>

          {showIconField && (
            <Col xs={12} md={6} className="mb-3">
              <label className="form-label">Category Icon</label>
              <UploadImage
                onFileSelect={setSelectedIcon}
                existingImages={existingIcon}
                multiple={false}
              />
            </Col>
          )}

          <Col xs={12} className="mb-3">
            <label className="form-label">Title *</label>
            <FormInput
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </Col>

          <Col md={6} className="mb-3">
            <label className="form-label">Subtitle *</label>
            <FormInput
              name="sub_title"
              value={formData.sub_title}
              onChange={handleChange}
            />
          </Col>

          <Col md={6} className="mb-3">
            <label className="form-label">Select Menu (Optional)</label>
            <AsyncSelect
              cacheOptions
              defaultOptions={true}   // 🔥 IMPORTANT FIX
              isClearable
              loadOptions={loadMenuOptions}
              value={selectedMenu}
              placeholder="Select Menu"
              onChange={(option) => {
                setSelectedMenu(option);
                setFormData((prev) => ({
                  ...prev,
                  menu_id: option ? option.value : null,
                }));
              }}
            />
          </Col>

          <Col xs={12} className="mb-3">
            <label className="form-label">Description *</label>
            <FormInput
              type="textarea"
              name="text"
              value={formData.text}
              onChange={handleChange}
            />
          </Col>

          <Col xs={12} className="mb-3">
            <label className="form-label">Slug</label>
            <FormInput
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              required
            />
          </Col>

          <Col xs={12}>
            <Button type="submit" disabled={loading}>
              {loading ? "Updating..." : "Update Category"}
            </Button>
          </Col>
        </Row>
      </form>
    </>
  );
};

export default UpdateProductListing;
